/**
 * v101.114.0 · Cajas de la red como POSTES sobre un mapa Leaflet (War Room, detalle de OT, aprovisionamiento).
 *  · Más visibles que v101.108.0: poste con borde blanco, caja de color (azul = distribución, azul oscuro =
 *    transmisión TD) y punto verde si tiene puertos libres.
 *  · Se muestran (a) al acercar el mapa (desde `minZoom`, solo el área visible) y (b) alrededor de los puntos de
 *    `focus` (técnico u OT seleccionados, cliente de la OT) a cualquier zoom.
 *  · `highlight`: nombres de cajas a resaltar (p. ej. la caja actual de la OT) → poste grande con aro amarillo y nombre.
 *  · `items`: lista propia de cajas (p. ej. las del buscador de aprovisionamiento) → no consulta el servidor.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import api from "../lib/api";

const _cache = {};
export function poleIcon(kind, { free = false, highlight = false } = {}) {
  const key = `${kind}|${free ? 1 : 0}|${highlight ? 1 : 0}`;
  if (_cache[key]) return _cache[key];
  const pole = "#475569";
  const box = kind === "td" ? "#0A3747" : "#2563EB";
  const w = highlight ? 26 : 18;
  const h = highlight ? 42 : 30;
  const ring = highlight
    ? `<circle cx="12" cy="12" r="10.5" fill="#FACC15" fill-opacity=".35" stroke="#CA8A04" stroke-width="1.6"/>` : "";
  const dot = free ? `<circle cx="17.2" cy="8.6" r="2.6" fill="#22C55E" stroke="#fff" stroke-width="1"/>` : "";
  const html = `<div style="filter:drop-shadow(0 1px 1.5px rgba(0,0,0,.55))">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 40" width="${w}" height="${h}">
    ${ring}
    <ellipse cx="12" cy="38.6" rx="4.2" ry="1.3" fill="#000" opacity=".3"/>
    <rect x="10.2" y="2" width="3.6" height="37" rx="1.4" fill="${pole}" stroke="#fff" stroke-width="1.2"/>
    <rect x="3" y="4.2" width="18" height="2.6" rx="1.2" fill="${pole}" stroke="#fff" stroke-width="1"/>
    <rect x="5" y="8" width="14" height="9.5" rx="2" fill="${box}" stroke="#fff" stroke-width="1.4"/>
    <rect x="7.2" y="10.4" width="9.6" height="1.6" rx=".8" fill="#fff" opacity=".7"/>
    ${dot}
  </svg></div>`;
  _cache[key] = L.divIcon({ html, className: "", iconSize: [w, h], iconAnchor: [w / 2, h - 1], tooltipAnchor: [0, -h + 6] });
  return _cache[key];
}

const norm = (s) => String(s || "").trim().toUpperCase();

export default function BoxPolesLayer({
  enabled = true, minZoom = 15, focus = [], radiusM = 600, highlight = [], items = null,
  onCount, onSelect, labelHighlighted = true,
}) {
  const map = useMap();
  const [viewItems, setViewItems] = useState([]);
  const [focusItems, setFocusItems] = useState([]);
  const tRef = useRef(null);
  const reqRef = useRef(0);
  const hl = useMemo(() => new Set((highlight || []).filter(Boolean).map(norm)), [highlight]);
  const focusKey = (focus || []).filter((p) => p && p.lat != null && p.lon != null).slice(0, 8)
    .map((p) => `${Number(p.lat).toFixed(5)},${Number(p.lon).toFixed(5)}`).join("|");

  // (a) Área visible al acercar
  const fetchView = useCallback(() => {
    if (!enabled || items || map.getZoom() < minZoom) { setViewItems([]); return; }
    const b = map.getBounds();
    const id = ++reqRef.current;
    api.get("/v1/warroom/cc/boxes", { params: { south: b.getSouth(), west: b.getWest(), north: b.getNorth(), east: b.getEast() } })
      .then(({ data }) => { if (id === reqRef.current) setViewItems(data.items || []); })
      .catch(() => { /* silencioso: las cajas son referencia visual */ });
  }, [enabled, items, map, minZoom]);
  useEffect(() => {
    const h = () => { clearTimeout(tRef.current); tRef.current = setTimeout(fetchView, 350); };
    map.on("moveend", h);
    fetchView();
    return () => { map.off("moveend", h); clearTimeout(tRef.current); };
  }, [map, fetchView]);

  // (b) Alrededor de los puntos seleccionados (cualquier zoom)
  useEffect(() => {
    if (!enabled || items || !focusKey) { setFocusItems([]); return undefined; }
    let alive = true;
    const pts = focusKey.split("|").map((x) => x.split(",").map(Number));
    Promise.all(pts.map(([lat, lon]) => api.get("/v1/warroom/cc/boxes", { params: { lat, lon, radius_m: radiusM } })
      .then(({ data }) => data.items || []).catch(() => [])))
      .then((lists) => { if (alive) setFocusItems(lists.flat()); });
    return () => { alive = false; };
  }, [enabled, items, focusKey, radiusM]);

  const all = useMemo(() => {
    if (!enabled) return [];
    const src = items || [...viewItems, ...focusItems];
    const seen = new Map();
    for (const x of src) {
      const lat = x.lat ?? x.latitude;
      const lon = x.lon ?? x.longitude;
      if (lat == null || lon == null) continue;
      const k = x.id ?? `${x.name}|${lat}|${lon}`;
      if (!seen.has(k)) seen.set(k, { ...x, lat: Number(lat), lon: Number(lon) });
    }
    return [...seen.values()];
  }, [enabled, items, viewItems, focusItems]);

  useEffect(() => { if (onCount) onCount(enabled ? all.length : null); }, [all.length, enabled, onCount]);

  if (!enabled) return null;
  return all.map((x) => {
    const isHl = hl.has(norm(x.name));
    const kind = x.kind || (String(x.equipment_type || "").includes("transmission") ? "td" : "dist");
    const free = x.is_free === true || x.has_free_ports === true;
    return (
      <Marker key={`bx-${x.id ?? x.name}`} position={[x.lat, x.lon]} icon={poleIcon(kind, { free, highlight: isHl })}
        zIndexOffset={isHl ? 700 : -200}
        eventHandlers={onSelect ? { click: () => onSelect(x) } : undefined}>
        <Tooltip direction="top" permanent={isHl && labelHighlighted} offset={[0, -4]}>
          <div className="text-[10px]">
            <b>{x.name}</b>{isHl ? " · caja de la OT" : ""}<br />
            {kind === "td" ? "Caja de transmisión (TD)" : "Caja de distribución"}
            {free ? " · con puertos libres" : ""}
            {x.distance_m != null ? ` · ${x.distance_m} m` : ""}
            {onSelect ? <><br /><span className="text-blue-600">Clic para elegirla</span></> : null}
          </div>
        </Tooltip>
      </Marker>
    );
  });
}
